import { RpServiceConfiguration, PartiallyFormattedRpServiceConfiguration, FormattedRpServiceConfigurationProperty } from "@idw-types/data/rp-service-configuration";
import { AxnAvailablePropertyCategory, AxnCompany, AxnIdentityProvider } from "@idw-types/data/reference-data/axn";
import { formatAppliedProfiles } from "../applied-profiles/applied-profiles-formatter";
import { extractAvailablePropertiesFromCategories } from "@utils/available-properties-util";
import { formatRpServiceConfigurationAttributes } from "@formatters/rp-services/rp-service-configuration-attribute-formatter";
import {
    AttributeClassification,
    AttributeLabel,
    AttributeType,    
    RpServiceAttributeProperty,    
    RpServiceAttributePropertyMap
} from "@idw-types/data/reference-data/generic";
import { formatRpServiceConfigurationProperties } from "@formatters/rp-services/rp-service-configuration-property-formatter";
import { getAxnuiContent } from "@utils/content-util";
import { AXNUIContent } from "@iddataweb/idw-common-ui/types";

export const partiallyFormatRpServiceConfiguration = (
    rpServiceConfiguration: RpServiceConfiguration,
    axnIdentityProviders: Array<AxnIdentityProvider>,
    axnCompanies: Array<AxnCompany>,
    axnAvailablePropertyCategories: Array<AxnAvailablePropertyCategory>,
    attributeTypes: Array<AttributeType>,
    attributeLabels: Array<AttributeLabel>,
    attributeClassifications: Array<AttributeClassification>,
    rpServiceAttributeProperties: Array<RpServiceAttributeProperty>,
    rpServiceAttributePropertyMaps: Array<RpServiceAttributePropertyMap>
): PartiallyFormattedRpServiceConfiguration => {
    const axnIdps = rpServiceConfiguration.idps.map(idp => axnIdentityProviders
        .find(axnIdp => axnIdp.id === idp.identityProviderID))
        .filter(x => x);

    const availableProperties = extractAvailablePropertiesFromCategories(axnAvailablePropertyCategories);

    const formattedAppliedProfiles = formatAppliedProfiles(
        rpServiceConfiguration.appliedProfiles,
        axnCompanies,
        availableProperties,
        false
    );

    const formattedProperties: Array<FormattedRpServiceConfigurationProperty> = formatRpServiceConfigurationProperties(
        rpServiceConfiguration.properties,
        availableProperties
    );

    const autoSubmitFlagAttributeProperty = rpServiceAttributeProperties.find(ap => ap.name === "autoSubmitFlag");
    const doNotSaveToPDSAttributeProperty = rpServiceAttributeProperties.find(ap => ap.name === "doNotSaveToPDS");
    const ignorePDSDataAttributeProperty = rpServiceAttributeProperties.find(ap => ap.name === "ignorePDSData");
    const isAsyncAttributeProperty = rpServiceAttributeProperties.find(ap => ap.name === "isAsync");
    const optionalFlagAttributeProperty = rpServiceAttributeProperties.find(ap => ap.name === "optionalFlag");
    const placeholderFlagAttributeProperty = rpServiceAttributeProperties.find(ap => ap.name === "placeholderFlag");
    const requiredForSelfServiceVerificationAttributeProperty = rpServiceAttributeProperties.find(ap => ap.name === "requiredForSelfServiceVerification");
    const userEditableFlagAttributeProperty = rpServiceAttributeProperties.find(ap => ap.name === "userEditableFlag");
    const userVisibleFlagAttributeProperty = rpServiceAttributeProperties.find(ap => ap.name === "userVisibleFlag");
    const customToolTipAttributeProperty = rpServiceAttributeProperties.find(ap => ap.name === "customToolTip");
    const displayFullValueAttributeProperty = rpServiceAttributeProperties.find(ap => ap.name === "displayFullValue");

    const axnuiContent: AXNUIContent = getAxnuiContent(formattedProperties);

    const formattedAttributes = formatRpServiceConfigurationAttributes(
        rpServiceConfiguration.attributes || [],
        attributeTypes,
        attributeLabels,
        attributeClassifications,
        rpServiceAttributePropertyMaps,
        autoSubmitFlagAttributeProperty,
        doNotSaveToPDSAttributeProperty,
        ignorePDSDataAttributeProperty,
        isAsyncAttributeProperty,
        optionalFlagAttributeProperty,
        placeholderFlagAttributeProperty,
        requiredForSelfServiceVerificationAttributeProperty,
        userEditableFlagAttributeProperty,
        userVisibleFlagAttributeProperty,
        customToolTipAttributeProperty,
        displayFullValueAttributeProperty,
        axnuiContent
    );


    return {
        ...rpServiceConfiguration,
        idps: axnIdps,
        appliedProfiles: formattedAppliedProfiles,
        attributes: formattedAttributes,
        formattedProperties
    };
};